// Migrated from /pages/installations.tsx for Phase 5 feature organization
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Search, Wrench, Plus, Calendar as CalendarIcon, Grid, KanbanSquare, ClipboardList, FileCheck, CheckCircle } from 'lucide-react';
import InstallationGrid from '@/components/installations/installation-grid';
import InstallationKanban from '@/components/installations/installation-kanban';
import InstallationCalendar from '@/components/installations/installation-calendar';

interface Installation {
  id: number;
  customerId: number;
  proposalId: number | null;
  status: string | null;
  scheduledDate: Date | null;
  completedDate: Date | null;
  notes: string | null;
  createdAt: Date | null;
  schedulingRequired: boolean | null;
  proposal?: { title: string; totalPrice: number | null };
}

export default function InstallationsPage() {
  const [viewMode, setViewMode] = useState<'grid' | 'kanban' | 'calendar'>('grid');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce the global search to avoid spamming the API
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data: installations, isLoading } = useQuery<Installation[]>({
    queryKey: ['/api/installations'],
  });

  const stats = {
    needsScheduling: installations?.filter((i) => i.schedulingRequired === true).length || 0,
    surveysPending: installations?.filter((i) =>
      i.status === 'waiting_site_survey' || i.status === 'site_survey'
    ).length || 0,
    dnoPending: installations?.filter((i) => i.status === 'g99').length || 0,
    readyToInstall: installations?.filter((i) => i.status === 'prepared_to_install').length || 0,
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Wrench className="h-8 w-8" />
          Installations
        </h1>
        <p className="text-muted-foreground">
          Track and manage solar panel installations from scheduling to completion
        </p>
      </div>

      {/* Action-Oriented Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Need Scheduling</CardTitle>
            <CalendarIcon className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.needsScheduling}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Surveys Pending</CardTitle>
            <ClipboardList className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.surveysPending}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">DNO Pending</CardTitle>
            <FileCheck className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.dnoPending}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ready to Install</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.readyToInstall}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="card-modern">
        <CardContent className="card-content py-2">
          <div className="flex items-center gap-2 justify-between">
            {/* Global search across all views */}
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search (name, postcode, address, refs)"
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <div className="inline-flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="h-4 w-4 mr-1" />
                Grid
              </Button>
              <Button
                variant={viewMode === 'kanban' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none"
                onClick={() => setViewMode('kanban')}
              >
                <KanbanSquare className="h-4 w-4 mr-1" />
                Kanban
              </Button>
              <Button
                variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none"
                onClick={() => setViewMode('calendar')}
              >
                <CalendarIcon className="h-4 w-4 mr-1" />
                Calendar
              </Button>
              </div>
              <Button asChild>
                <Link href="/installations/new">
                  <Plus className="h-4 w-4 mr-2" />
                  New Installation
                </Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {viewMode === 'grid' ? (
        <InstallationGrid search={debouncedSearch} />
      ) : viewMode === 'kanban' ? (
        <InstallationKanban search={debouncedSearch} />
      ) : (
        <InstallationCalendar search={debouncedSearch} />
      )}
    </div>
  );
}
